import React, { useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { ApprovalLevel } from './ApprovalLevel';
import {
  newApprovalLevelDto,
  ApprovalLevelDto,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/ApprovalLevelDto';
import './ApprovalChain.less';
import { swap, isNotEmpty } from 'utils/ArrayUtils';
import { generateGuid } from 'utils/Guid';
import { AssigneeDto } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Assignments';
import { getRuleAsText } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Approvals/ApprovalChainEntityService';

export interface IApprovalChainProps extends Omit<React.HTMLProps<HTMLDivElement>, 'onChange'> {
  levels: ApprovalLevelDto[];
  /**
   * Determines whether show "Edit Rule Section"
   */
  allowRules: boolean;
  /**
   * Determines whether show "Expiration section" which will allow to edit "time to approve"
   */
  allowExpiration: boolean;
  onChange: (levels: ApprovalLevelDto[]) => void;
  /**
   * Will be called when clicking on "Edit Expiration".
   * Should contain edit expiration implementation and return new value.
   */
  onEditExpiration: (levelNumber: number, level: ApprovalLevelDto) => Promise<string | null>;
  /**
   * Will be called when clicking on "Edit Rule".
   * Should contain edit rule implementation and return new value.
   *
   * @returns if there is no rule, returns created rule id. If rule already exists, returns existing rule id.
   */
  onEditRule: (levelNumber: number, level: ApprovalLevelDto) => Promise<string>;
}

const getClassName = (className?: string) => classNames('dpc-approval-chain', className);

function getLevelsOrDefault(levels: ApprovalLevelDto[]) {
  return isNotEmpty(levels) ? levels : [newApprovalLevelDto({ ApprovalDataId: generateGuid() })];
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ApprovalChain({
  levels: _levels,
  allowExpiration,
  allowRules,
  className,
  onChange,
  onEditExpiration,
  onEditRule,
  ...htmlOptions
}: IApprovalChainProps) {
  const [levels, setLevels] = useState(getLevelsOrDefault(_levels));

  // force to update state if properties were changed
  useEffect(() => {
    setLevels(getLevelsOrDefault(_levels));
  }, [_levels]);

  const handleLevelDown = useCallback(
    (levelNumber: number) => {
      const newLevels = [...levels];

      swap(newLevels, levelNumber - 1, levelNumber);
      setLevels(newLevels);
      onChange(newLevels);
    },
    [levels, onChange]
  );

  const handleLevelUp = useCallback(
    (levelNumber: number) => {
      const newLevels = [...levels];

      swap(newLevels, levelNumber - 1, levelNumber - 2);
      setLevels(newLevels);
      onChange(newLevels);
    },
    [levels, onChange]
  );

  const handleDelete = useCallback(
    (_: any, level: ApprovalLevelDto) => {
      const newLevels = levels.filter(item => item.ApprovalDataId !== level.ApprovalDataId);

      setLevels(newLevels);
      onChange(newLevels);
    },
    [levels, onChange]
  );

  const handleChangeAssignees = useCallback(
    (assignees: AssigneeDto[], levelNumber: number) => {
      const newLevels = [...levels];

      newLevels[levelNumber - 1].Approvers = assignees;
      setLevels(newLevels);
      onChange(newLevels);
    },
    [levels, onChange]
  );

  const handleAddLevel = useCallback(() => {
    const newLevels = [...levels];

    newLevels.push(newApprovalLevelDto({ ApprovalDataId: generateGuid() }));

    setLevels(newLevels);
    onChange(newLevels);
  }, [levels, onChange]);

  const handleEditExpiration = useCallback(
    (levelNumber: number, level: ApprovalLevelDto) => {
      onEditExpiration(levelNumber, level).then(value => {
        const newLevels = [...levels];

        newLevels[levelNumber - 1].TimeToApprove = value;

        setLevels(newLevels);
        onChange(newLevels);
      });
    },
    [levels, onChange, onEditExpiration]
  );

  const handleEditRule = useCallback(
    (levelNumber: number, level: ApprovalLevelDto) => {
      onEditRule(levelNumber, level).then(async ruleId => {
        const newLevels = [...levels];

        const ruleText = await getRuleAsText({ ruleId });
        newLevels[levelNumber - 1] = { ...newLevels[levelNumber - 1], ...{ RuleId: ruleId, RuleText: ruleText } };

        setLevels(newLevels);
        onChange(newLevels);
      });
    },
    [levels, onChange, onEditRule]
  );

  return (
    <div className={getClassName()} {...htmlOptions}>
      <ul className="dpc-approval-chain__levels">
        {(levels ?? []).map((item, index) => (
          <ApprovalLevel
            data-testid={`approval chain ${index + 1}`}
            key={index}
            level={item}
            levelNumber={index + 1}
            canDelete={levels.length > 1}
            canMoveDown={index !== levels.length - 1}
            canMoveUp={index !== 0}
            allowExpiration={allowExpiration}
            allowRules={allowRules}
            onLevelDown={handleLevelDown}
            onLevelUp={handleLevelUp}
            onDelete={handleDelete}
            onAssigneesChanged={handleChangeAssignees}
            onEditExpiration={handleEditExpiration}
            onEditRule={handleEditRule}
          />
        ))}
      </ul>
      <div className="dpc-approval-chain__add-level-container flex-centering">
        <button className="dpc-approval-chain__add-level dp-link" onClick={handleAddLevel}>
          Add Level
        </button>
      </div>
    </div>
  );
}
